import { useCallback, useEffect, useState } from 'react'

interface Props {
  syncGreenMapping: (currentState: boolean) => void
  syncHoleTool: (currentState: boolean) => void
}

export default function useGpsEditorToolModes({ syncGreenMapping, syncHoleTool }: Props): {
  greenMapping: boolean
  holeToolEnabled: boolean
  selectEditTool: () => void
  toggleGreenMapping: () => void
  toggleHoleTool: () => void
} {
  const [holeToolEnabled, setHoleToolEnabled] = useState<boolean>(false)
  const [greenMapping, setGreenMapping] = useState<boolean>(false)

  const toggleHoleTool = useCallback(() => {
    const next = !holeToolEnabled
    setHoleToolEnabled(next)
    syncHoleTool(next)

    if (next) {
      setGreenMapping(false)
      syncGreenMapping(false)
    }
  }, [holeToolEnabled, syncGreenMapping, syncHoleTool])

  const toggleGreenMapping = useCallback(() => {
    const next = !greenMapping
    setGreenMapping(next)
    syncGreenMapping(next)

    if (next) {
      setHoleToolEnabled(false)
      syncHoleTool(false)
    }
  }, [greenMapping, syncGreenMapping, syncHoleTool])

  const selectEditTool = useCallback(() => {
    setHoleToolEnabled(false)
    syncHoleTool(false)
    setGreenMapping(false)
    syncGreenMapping(false)
  }, [syncGreenMapping, syncHoleTool])

  const handleToolHotkeys = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const targetTagName = target?.tagName.toLowerCase()
      const isTypingField =
        targetTagName === 'input' ||
        targetTagName === 'textarea' ||
        targetTagName === 'select' ||
        Boolean(target?.isContentEditable)

      if (isTypingField || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      const key = event.key.toLowerCase()
      if (key === 'e') {
        selectEditTool()
        return
      }

      if (key === 'h') {
        toggleHoleTool()
        return
      }

      if (key === 'g') {
        toggleGreenMapping()
      }
    },
    [selectEditTool, toggleGreenMapping, toggleHoleTool],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleToolHotkeys)
    return () => {
      document.removeEventListener('keydown', handleToolHotkeys)
    }
  }, [handleToolHotkeys])

  return {
    greenMapping,
    holeToolEnabled,
    selectEditTool,
    toggleGreenMapping,
    toggleHoleTool,
  }
}
